import { Contact, UpdateAddressContactInput } from '@/gql/graphql';
import { Button, Card, Conditional, Field, Input } from '@payaca/ambrosia';

import ValidatedForm, {
  TRenderFormContents,
} from '@payaca/components/validatedForm/ValidatedForm';
import { getIsRequiredFieldValidator } from '@payaca/helpers/fieldValidationHelper';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

export interface IProps {
  contacts: Contact[];
  onUpdate?: (contacts: UpdateAddressContactInput[]) => void;
}

type TContactFormState = Omit<Contact, 'id' | 'publicId'>;

// Map a Contact down to the fields the server accepts. Contact carries
// publicId and __typename, neither of which exist on UpdateAddressContactInput,
// so spreading a whole Contact into the mutation input is rejected by the API.
const toUpdateAddressContactInput = (
  contact: Partial<Contact>
): UpdateAddressContactInput => ({
  id: contact.id,
  name: contact.name,
  email: contact.email,
  phone: contact.phone,
  description: contact.description,
});

type ContactFormProps = {
  isEditMode?: boolean;
  contact?: Omit<Contact, 'id' | 'publicId'>;
  onUpdate?: (state: TContactFormState) => void;
  onRemove?: () => void;
  onCancel?: () => void;
};

const ContactForm: FC<ContactFormProps> = (props) => {
  const {
    isEditMode: _isEditMode = false,
    contact,
    onUpdate,
    onRemove,
    onCancel,
  } = props;
  const [isEditMode, setIsEditMode] = useState(_isEditMode);

  const initialState = useMemo<TContactFormState>(() => {
    return {
      name: contact?.name || '',
      email: contact?.email || '',
      phone: contact?.phone || '',
      description: contact?.description || '',
    };
  }, [contact]);

  const fieldValidators = useMemo(
    () => ({
      name: [getIsRequiredFieldValidator()],
    }),
    []
  );

  const renderFormContents = useCallback<
    TRenderFormContents<TContactFormState>
  >(
    (
      isValid,
      formState,
      validationState,
      touchedState,
      onFieldChange,
      onFieldTouch
    ) => {
      return (
        <div className="space-y-4">
          <Field.Legacy
            name="name"
            validationState={
              !validationState?.['name']?.isValid && touchedState?.['name']
                ? {
                    isValid: false,
                    validationMessages: validationState?.['name']?.errors,
                  }
                : undefined
            }
          >
            <Field.Label>Name</Field.Label>
            <Input
              value={formState.name || ''}
              onChange={(value) => onFieldChange({ name: value })}
              onTouch={() => onFieldTouch('name')}
            />
          </Field.Legacy>
          <Field.Legacy name="email">
            <Field.Label>Email</Field.Label>
            <Input
              value={formState.email || ''}
              onChange={(value) => onFieldChange({ email: value })}
            />
          </Field.Legacy>
          <Field.Legacy name="phone">
            <Field.Label>Phone</Field.Label>
            <Input
              value={formState.phone || ''}
              onChange={(value) => onFieldChange({ phone: value })}
            />
          </Field.Legacy>
          <Field.Legacy name="description">
            <Field.Label>Description</Field.Label>
            <Input
              value={formState.description || ''}
              onChange={(value) => onFieldChange({ description: value })}
            />
          </Field.Legacy>
          <div className="space-x-2">
            <Button
              disabled={!isValid}
              size={'sm'}
              onClick={() => {
                onUpdate?.(formState);
                setIsEditMode(false);
              }}
            >
              Save
            </Button>
            <Button
              size={'sm'}
              variant="secondary"
              onClick={() => {
                setIsEditMode(false);
                onCancel?.();
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      );
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'onUpdate', and 'onCancel'
    []
  );

  if (!isEditMode) {
    return (
      <Card>
        <Card.Body>
          <div className="flex flex-row items-stretch gap-2">
            <div className="grow">
              <div className="text-base">
                {!!contact?.name?.length && (
                  <p className="mb-4 text-base font-medium">{contact.name}</p>
                )}
                <div className="text-gray-500">
                  {!!contact?.email?.length && (
                    <p className="mb-1 text-base">{contact.email}</p>
                  )}
                  {!!contact?.phone?.length && (
                    <p className="mb-0 text-base">{contact.phone}</p>
                  )}
                </div>
                {!!contact?.description?.length && (
                  <p className="mb-0 mt-4 text-base text-gray-500">
                    {contact.description}
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end justify-between gap-2">
              <Button variant="link" onClick={onRemove}>
                Remove
              </Button>
              <Button
                onClick={() => setIsEditMode(true)}
                variant="secondary"
                size={'xs'}
              >
                Edit
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Body>
        <ValidatedForm<TContactFormState>
          initialFormState={initialState}
          renderFormContents={renderFormContents}
          fieldValidators={fieldValidators}
        />
      </Card.Body>
    </Card>
  );
};

const AddEditAddressContactsControl: FC<IProps> = (props) => {
  const { contacts: initialContacts, onUpdate } = props;
  const [contacts, setContacts] = useState(initialContacts);
  const [isAddingContact, setIsAddingContact] = useState(false);

  useEffect(() => {
    setContacts(initialContacts);
  }, [initialContacts]);

  const handleUpdateContact = useCallback(
    (contactId: string) => (updatedContact: TContactFormState) => {
      setContacts((_contacts) => {
        const updatedContacts = _contacts.map((i) =>
          // Keep the existing contact's id so the server can identify the row;
          // the form only edits name/email/phone/description.
          i.id === contactId ? { ...i, ...updatedContact } : i
        );

        onUpdate?.(updatedContacts.map(toUpdateAddressContactInput));

        return updatedContacts;
      });
    },
    [onUpdate]
  );

  const handleDeleteContact = useCallback(
    (contactId: string) => () => {
      setContacts((_contacts) => {
        const updatedContacts = _contacts.filter((i) => i.id !== contactId);

        onUpdate?.(updatedContacts.map(toUpdateAddressContactInput));

        return updatedContacts;
      });
    },
    [onUpdate]
  );

  return (
    <>
      <Field.Legacy>
        <Field.Label>Access contacts</Field.Label>

        <div className="space-y-4">
          {contacts.map((contact) => (
            <ContactForm
              key={contact.id}
              contact={contact}
              onUpdate={handleUpdateContact(contact.id)}
              onRemove={handleDeleteContact(contact.id)}
            />
          ))}

          <Conditional condition={isAddingContact}>
            <ContactForm
              isEditMode
              onUpdate={(updatedContact) => {
                onUpdate?.(
                  [...contacts, updatedContact].map(toUpdateAddressContactInput)
                );
                setIsAddingContact(false);
              }}
              onCancel={() => setIsAddingContact(false)}
            />
          </Conditional>
        </div>
      </Field.Legacy>
      <div>
        <Button onClick={() => setIsAddingContact(true)} variant="link">
          Add contact
        </Button>
      </div>
    </>
  );
};

export default AddEditAddressContactsControl;
